import { Button } from "@/components/ui/button";
import { MbCard } from "@/components/MbCard";
import { MbBadgeSun } from "@/components/MbBadgeSun";
import { useNavigate } from "react-router-dom";
import { Sprout, Users, BookOpen, MessageCircle, Sun, Leaf } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AppFooter } from "@/components/AppFooter";

export default function Landing() {
  const navigate = useNavigate();
  const { logo, strings, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))]">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Smart Farmer" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button variant="outline" onClick={() => navigate("/auth")}>
              {strings.login || 'લૉગિન'}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Animated Sun */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--mb-sun))_0%,hsl(46_85%_70%)_100%)] animate-pulse" />
              <Sun className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 text-white" />
            </div>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-[hsl(var(--mb-green-dark))] leading-tight">
            {strings.tagline}
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
            {language === 'hi' 
              ? 'आधुनिक और वैदिक तरीकों से अपनी खेती को और सफल बनाएं'
              : 'આધુનિક અને વૈદિક પદ્ધતિઓથી તમારી ખેતીને વધુ સફળ બનાવો'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              variant="hero" 
              size="xl"
              onClick={() => navigate("/subscribe")}
              className="w-full sm:w-auto"
            >
              {strings.ctaBecome}
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate("/auth")}
              className="w-full sm:w-auto"
            >
              {language === 'hi' ? 'पहले से सदस्य हैं?' : 'પહેલેથી જ સભ્ય છો?'}
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <MbCard elevated className="p-6 text-center space-y-4 hover:scale-105 transition-transform">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Sprout className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold">{strings.benefits.modern}</h3>
            <p className="text-muted-foreground">{language === 'hi' ? 'विज्ञान आधारित खेती' : 'વિજ્ઞાન આધારિત ખેતી'}</p>
          </MbCard>

          <MbCard elevated className="p-6 text-center space-y-4 hover:scale-105 transition-transform">
            <div className="w-16 h-16 mx-auto rounded-full bg-secondary/20 flex items-center justify-center">
              <Leaf className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="text-xl font-bold">{strings.benefits.vedic}</h3>
            <p className="text-muted-foreground">{language === 'hi' ? 'पारंपरिक ज्ञान' : 'પરંપરાગત જ્ઞાન'}</p>
          </MbCard>

          <MbCard elevated className="p-6 text-center space-y-4 hover:scale-105 transition-transform">
            <div className="w-16 h-16 mx-auto rounded-full bg-accent/20 flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-accent-foreground" />
            </div>
            <h3 className="text-xl font-bold">{strings.benefits.personal}</h3>
            <p className="text-muted-foreground">{language === 'hi' ? 'सीधा संपर्क' : 'સીધો સંપર્ક'}</p>
          </MbCard>

          <MbCard elevated className="p-6 text-center space-y-4 hover:scale-105 transition-transform">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold">{strings.benefits.community}</h3>
            <p className="text-muted-foreground">{language === 'hi' ? 'किसान समुदाय' : 'ખેડૂત સમુદાય'}</p>
          </MbCard>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <MbCard elevated className="overflow-hidden">
            <div className="bg-gradient-to-r from-[hsl(var(--mb-green-dark))] to-[hsl(var(--mb-green-light))] p-8 text-white text-center">
              <MbBadgeSun className="mb-4">{language === 'hi' ? 'प्रीमियम' : 'પ્રીમિયમ'}</MbBadgeSun>
              <h2 className="text-3xl font-bold mb-2">{strings.pricing.title}</h2>
              <div className="text-5xl font-bold mb-4">{strings.pricing.price}</div>
            </div>
            <div className="p-8 space-y-6">
              <ul className="space-y-4">
                {strings.pricing.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <BookOpen className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-lg">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                variant="hero" 
                size="lg" 
                className="w-full"
                onClick={() => navigate("/subscribe")}
              >
                {strings.ctaBecome}
              </Button>
            </div>
          </MbCard>
        </div>
      </section>

      <AppFooter />
    </div>
  );
}
